import React from 'react';
import { Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { listCases, runCases, listRuns, type TestCaseDef, type TestRunSummary } from '../services/testingService';

const TestingPage: React.FC = () => {
  const [cases, setCases] = React.useState<TestCaseDef[]>([]);
  const [selected, setSelected] = React.useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [lastRun, setLastRun] = React.useState<TestRunSummary | null>(null);
  const [runs, setRuns] = React.useState<TestRunSummary[]>([]);

  const load = React.useCallback(async () => {
    const [c, r] = await Promise.all([listCases(), listRuns()]);
    setCases(c);
    setRuns(r);
    if (c && c.length && Object.keys(selected).length === 0) {
      const init: Record<string, boolean> = {};
      c.forEach(x => init[x.id] = true);
      setSelected(init);
    }
  }, [selected]);

  React.useEffect(() => { load().catch(() => {}); }, [load]);

  const toggle = (id: string) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const allSelected = React.useMemo(() => cases.every(c => selected[c.id]), [cases, selected]);
  const toggleAll = () => {
    const next = !allSelected;
    const map: Record<string, boolean> = {};
    cases.forEach(c => map[c.id] = next);
    setSelected(map);
  };

  const handleRun = async () => {
    setIsLoading(true);
    try {
      const picked = cases.filter(c => selected[c.id]).map(c => c.id);
      const summary = await runCases(picked);
      setLastRun(summary);
      const r = await listRuns();
      setRuns(r);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Testing — smoke testy aplikacji</Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={allSelected} onChange={toggleAll} />}
              label="Zaznacz wszystko"
            />
            <Button variant="contained" onClick={handleRun} disabled={isLoading}>
              {isLoading ? <CircularProgress size={20} /> : 'Uruchom testy'}
            </Button>
          </Stack>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {cases.map((c) => (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox checked={!!selected[c.id]} onChange={() => toggle(c.id)} />
                      <Box>
                        <Typography variant="subtitle1">{c.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{c.description}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {lastRun && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">Ostatni run</Typography>
            <Typography variant="body2" color="text.secondary">Start: {lastRun.startedAt} {lastRun.endedAt ? `— Koniec: ${lastRun.endedAt}` : ''}</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {lastRun.results.map(r => (
                <Grid item xs={12} md={6} key={r.id}>
                  <Card variant="outlined" sx={{ borderColor: r.ok ? 'success.main' : 'error.main' }}>
                    <CardContent>
                      <Typography variant="subtitle1">{r.id} — {r.ok ? 'OK' : 'BŁĄD'}</Typography>
                      <Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, mt: 1 }}>
                        {r.log?.join('\n')}
                        {r.error ? `\nERROR: ${r.error}` : ''}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6">Historia uruchomień</Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {runs.map(r => (
              <Grid item xs={12} md={6} key={(r as any).runId || (r as any).id || Math.random()}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1">{(() => {
                      const rid = (r as any).runId || (r as any).id || '';
                      const short = typeof rid === 'string' ? rid.slice(0, 8) : '';
                      return `Run ${short}${short ? '…' : ''}`;
                    })()}</Typography>
                    <Typography variant="body2" color="text.secondary">{r.startedAt} {r.endedAt ? `— ${r.endedAt}` : ''}</Typography>
                    <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                      {r.results?.map(it => (
                        <li key={it.id}>{it.id}: {it.ok ? 'OK' : 'BŁĄD'}</li>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TestingPage;


